import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, FileText, Lock, LogOut, Mail, Shield, Star, User as UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotes } from '../hooks/useNotes';
import { useCollections } from '../hooks/useCollections';
import { useBilling, usePlans } from '../hooks/useBilling';
import { billingAction, shortPriceLabel } from '../lib/billing';
import { HIGHLIGHTERS } from '../lib/paper';
import { initials, libraryStats, stampFor } from '../lib/account';
import { EditEmailModal } from '../components/EditEmailModal';
import { EditNameModal } from '../components/EditNameModal';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { LibraryCard } from '../components/account/LibraryCard';
import { SettingGroup, SettingRow } from '../components/account/SettingRow';
import { EnterOnce } from '../components/paper';
import { toaster } from '../components/ui';

const PAGE = 'mx-auto max-w-[40rem] px-5 pb-16 pt-[calc(1.5rem+var(--mobile-nav-offset))] md:px-8 md:pt-14';

const formatDateTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

/** A line on what the subscription is doing, for the row under the plan. */
const statusLine = (status: string | null, cancelAtPeriodEnd: boolean): string | null => {
  if (status === 'active' && cancelAtPeriodEnd) return 'Ends at the close of this billing period';
  switch (status) {
    case 'trialing':
      return 'On your free trial';
    case 'past_due':
      return 'Your last payment failed';
    case 'incomplete':
    case 'incomplete_expired':
      return "Checkout didn't finish";
    case 'unpaid':
      return 'Unpaid';
    default:
      return null;
  }
};

/**
 * Account — the web twin of mobile's account screen (mobile/app/(app)/(tabs)/account.tsx):
 * the access card, then profile, subscription and fine-print setting groups.
 */
export const Account: React.FC = () => {
  const { user, logout, updateAccount } = useAuth();
  const navigate = useNavigate();
  const { notes, isLoading: notesLoading } = useNotes();
  const { collections, isLoading: collectionsLoading } = useCollections();
  const { plan } = usePlans();
  const { busy, subscribe, openPortal } = useBilling();
  const [nameOpen, setNameOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  if (!user) return null;

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
    : '—';
  const action = billingAction(user);
  const renewal = formatDateTime(user.current_period_end);
  const status = statusLine(user.subscription_status, user.cancel_at_period_end);

  return (
    <div className={PAGE}>
      <p className="pb-eyebrow m-0">Account</p>

      <EnterOnce id="account:card">
        <LibraryCard
          name={user.name || 'Reader'}
          email={user.email}
          monogram={initials(user.name, user.email)}
          cardNumber={`PB-${user.id}`}
          memberSince={memberSince}
          stamp={stampFor({ isSubscribed: user.is_subscribed, cancelAtPeriodEnd: user.cancel_at_period_end })}
          stats={libraryStats(notesLoading ? undefined : notes, collectionsLoading ? undefined : collections.length)}
        />
      </EnterOnce>

      <p className="pb-eyebrow m-0 mt-8">Profile</p>
      <SettingGroup>
        <SettingRow icon={UserIcon} tone={HIGHLIGHTERS.lilac} label="Name" value={user.name || 'Add'} onClick={() => setNameOpen(true)} />
        <SettingRow icon={Mail} tone={HIGHLIGHTERS.sky} label="Email" value={user.email} onClick={() => setEmailOpen(true)} />
        <SettingRow icon={Lock} tone={HIGHLIGHTERS.yellow} label="Password" value="Change" onClick={() => setPasswordOpen(true)} />
      </SettingGroup>

      <p className="pb-eyebrow m-0 mt-8">Subscription</p>
      <SettingGroup>
        {action === 'manage' && (
          <SettingRow
            icon={CreditCard}
            tone={HIGHLIGHTERS.mint}
            label="Manage subscription"
            value={renewal ? `${user.cancel_at_period_end ? 'Ends' : 'Renews'} ${renewal}` : undefined}
            onClick={() => void openPortal()}
            disabled={busy}
          />
        )}
        {action === 'fixPayment' && (
          <SettingRow
            icon={CreditCard}
            tone={HIGHLIGHTERS.coral}
            label="Update payment method"
            onClick={() => void openPortal()}
            disabled={busy}
          />
        )}
        {action === 'fixInStore' && (
          <SettingRow
            icon={CreditCard}
            tone={HIGHLIGHTERS.coral}
            label="Update payment in the App Store or Google Play"
          />
        )}
        {(action === 'trial' || action === 'subscribe') && (
          <SettingRow
            icon={Star}
            tone={HIGHLIGHTERS.pink}
            label={action === 'trial' && plan?.trial_days ? `Start your ${plan.trial_days}-day free trial` : 'Subscribe to PaperBrain'}
            value={plan ? shortPriceLabel(plan) : undefined}
            onClick={() => plan && void subscribe(plan.plan)}
            disabled={busy || !plan}
          />
        )}
        {status && <SettingRow icon={FileText} tone={HIGHLIGHTERS.coral} label={status} />}
      </SettingGroup>

      <p className="pb-eyebrow m-0 mt-8">The fine print</p>
      <SettingGroup>
        <SettingRow icon={FileText} tone={HIGHLIGHTERS.coral} label="Terms of Service" to="/terms" />
        <SettingRow icon={Shield} tone={HIGHLIGHTERS.mint} label="Privacy Policy" to="/privacy" />
        <SettingRow icon={CreditCard} tone={HIGHLIGHTERS.sky} label="Refund Policy" to="/refunds" />
      </SettingGroup>

      <button
        type="button"
        onClick={() => {
          logout();
          navigate('/login');
        }}
        className="mt-8 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-solid border-[rgba(179,38,30,0.4)] bg-transparent py-3.5 font-body text-[0.9375rem] font-semibold text-error transition-colors hover:bg-[rgba(179,38,30,0.05)] active:scale-[0.99]"
      >
        <LogOut size={16} /> Sign out
      </button>

      <p className="m-0 mt-6 text-center font-headline text-[0.8125rem] italic text-on-surface-variant">
        Made with graphite &amp; coffee
      </p>

      <EditNameModal
        isOpen={nameOpen}
        currentName={user.name}
        onClose={() => setNameOpen(false)}
        onSubmit={async (name) => {
          await updateAccount({ name });
          setNameOpen(false);
          toaster.success({ title: 'Name updated.' });
        }}
      />
      <EditEmailModal
        isOpen={emailOpen}
        currentEmail={user.email}
        onClose={() => setEmailOpen(false)}
        onSubmit={async (email) => {
          await updateAccount({ email });
          setEmailOpen(false);
          toaster.success({ title: 'Email updated.' });
        }}
      />
      <ChangePasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        onSubmit={async (currentPassword, newPassword) => {
          await updateAccount({ current_password: currentPassword, new_password: newPassword });
          setPasswordOpen(false);
          toaster.success({ title: 'Password updated.' });
        }}
      />
    </div>
  );
};
